import React, { Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { TextField } from '@nexds/web'

import { BaseUnit, Unit } from '@/domain/protocols/Unit'

import { Button, ButtonText, Container, HelpMessage, Label, UnitFieldInputContainer } from './UnitField.styles'

import { displayUnit } from '@/utils/unit'

export const baseUnitsMapper: Record<BaseUnit, Unit[]> = {
  speed: ['kt', 'km/h', 'mph'],
  direction: ['°M', '°T'],
  altitude: ['ft', 'm'],
  rate: ['ft/min', 'm/s'],
  fuelConsumption: ['l/h', 'kg/h', 'lb/h', 'imp-gal/h', 'us-gal/h'],
  fuelQuantity: ['l', 'kg', 'lb', 'imp-gal', 'us-gal'],
  volume: ['l', 'imp-gal', 'us-gal'],
  distance: ['nm', 'km'],
  weight: ['kg', 'lb'],
  linearMeasure: ['in', 'cm', 'm']
}

export interface UnitFieldProps {
  allowNegative?: boolean
  baseUnit?: BaseUnit
  currentUnit?: Unit
  customUnits?: string[]
  currentCustomUnit?: string
  customDisplayUnits?: string[]
  label?: string
  placeholder?: string
  value?: string
  autofocus?: boolean
  size?: 'sm' | 'md' | 'lg'
  helpMessage?: string
  error?: boolean
  helpGutter?: boolean
  onChangeUnit?: Dispatch<SetStateAction<Unit>>
  onChangeCustomUnit?: Dispatch<SetStateAction<string>> | ((newUnit: string) => void)
  // additional input props (optional) will be accepted via intersection below
}
type UnitFieldPropsExtended = UnitFieldProps & {
  onBlur?: (e: React.FocusEvent) => void
  onFocus?: (e: React.FocusEvent) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const UnitField = forwardRef<
  HTMLInputElement,
  UnitFieldPropsExtended & Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'size'>
>((props, ref) => {
  const {
    allowNegative,
    baseUnit,
    label,
    placeholder,
    value,
    currentUnit,
    currentCustomUnit,
    customDisplayUnits,
    customUnits,
    autofocus,
    error,
    size = 'sm',
    helpGutter,
    helpMessage,
    onChangeUnit,
    onChangeCustomUnit,
    // react-hook-form / input props
    name,
    onChange,
    onBlur,
    onFocus,
    ...rest
  } = props

  const usingCustom = Array.isArray(customUnits) && customUnits.length > 0
  const baseUnits = useMemo(() => (baseUnit ? baseUnitsMapper[baseUnit] : []), [baseUnit])

  const initialIndex = usingCustom
    ? customUnits!.findIndex((u) => currentCustomUnit === u)
    : baseUnits.findIndex((unit) => currentUnit === unit)

  const [unitIndex, setUnitIndex] = useState(initialIndex !== -1 ? initialIndex : 0)

  useEffect(() => {
    if (initialIndex !== -1) {
      setUnitIndex(initialIndex)
    } else {
      setUnitIndex(0)
    }
  }, [initialIndex])

  const handleSwitchUnit = useCallback(() => {
    if (usingCustom) {
      const nextIndex = unitIndex + 1 < customUnits!.length ? unitIndex + 1 : 0
      setUnitIndex(nextIndex)
      onChangeCustomUnit?.(customUnits![nextIndex])
    } else {
      if (!baseUnit) return
      const nextIndex = unitIndex + 1 < baseUnits.length ? unitIndex + 1 : 0
      setUnitIndex(nextIndex)
      onChangeUnit?.(baseUnits[nextIndex])
    }
  }, [unitIndex, usingCustom, customUnits, baseUnits, baseUnit, onChangeUnit, onChangeCustomUnit])

  const displayedUnit = useMemo(() => {
    if (usingCustom) {
      if (customDisplayUnits && customDisplayUnits.length > 0) {
        const customCurrentUnitIndex = customUnits!.findIndex((u) => currentCustomUnit === u)
        return customDisplayUnits[customCurrentUnitIndex !== -1 ? customCurrentUnitIndex : unitIndex]
      }
      // se não houver customDisplayUnits, mostra o próprio customUnits
      return customUnits ? customUnits[unitIndex] : ''
    } else {
      return currentUnit ? displayUnit.any(currentUnit) : displayUnit.any(baseUnits[unitIndex])
    }
  }, [usingCustom, customDisplayUnits, customUnits, unitIndex, currentCustomUnit, currentUnit, baseUnits])

  const switchDisabled = usingCustom ? !onChangeCustomUnit : !onChangeUnit

  return (
    <Container>
      <Label>{label}</Label>
      <UnitFieldInputContainer>
        <TextField
          // repassa ref e props de input para suportar react-hook-form register
          ref={ref}
          name={name}
          multiline={false}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          autoFocus={autofocus}
          className="unit-field-input"
          labelGutter={false}
          helpGutter={false}
          error={error}
          size={size}
          value={value}
          placeholder={placeholder ?? '0'}
          type="number"
          onWheel={(e) => e.currentTarget.blur()}
          min={allowNegative ? undefined : '0'}
          onKeyDown={(e) => {
            if ((allowNegative && e.key === '-') || e.key === 'e') {
              e.preventDefault()
            }
          }}
          {...rest}
        />

        <Button role="switch" type="button" size={size} disabled={switchDisabled} onClick={handleSwitchUnit}>
          <ButtonText size={size}>{displayedUnit}</ButtonText>
        </Button>
      </UnitFieldInputContainer>
      {helpGutter && <HelpMessage error={error}>{helpMessage}</HelpMessage>}
    </Container>
  )
})

UnitField.displayName = 'UnitField'

export { UnitField }
